import React from "react";
import type { AvailabilityBlock } from "@store/disponibiliteStore";
import type { WorkshopJob, WorkshopWorker } from "../../types/garage/atelier";
import { workerLoadHours, workerPresenceHours } from "@utils/workerPresence";
import { ATELIER_FILTER_METIERS, METIER_META, metierLabel } from "../../types/garage/metier";

function fmt(h: number): string {
  const r = Math.round(h * 4) / 4;
  const hours = Math.floor(r);
  const mins = Math.round((r - hours) * 60);
  return mins ? `${hours}h${String(mins).padStart(2, "0")}` : `${hours}h`;
}

type Load = { presence: number; load: number };

function loadColor({ presence, load }: Load): { ratio: number; color: string } {
  const ratio = presence > 0 ? load / presence : load > 0 ? 2 : 0;
  const color = ratio > 1 ? "#DC2626" : ratio >= 0.85 ? "#EA580C" : "#059669";
  return { ratio, color };
}

function loadTitle(name: string, { presence, load }: Load): string {
  if (presence === 0 && load === 0) return `${name} : aucune présence posée`;
  const rest = presence - load;
  return `${name} : ${fmt(load)} chargées sur ${fmt(presence)} de présence — ${
    rest >= 0 ? `reste ${fmt(rest)}` : `surcharge de ${fmt(-rest)}`
  }`;
}

function Gauge({ value }: { value: Load }) {
  const { ratio, color } = loadColor(value);
  return (
    <div style={{ height: 4, borderRadius: 2, background: "#F0F0F0", overflow: "hidden" }}>
      <div
        style={{
          width: `${Math.min(ratio, 1) * 100}%`,
          height: "100%",
          background: color,
          borderRadius: 2,
        }}
      />
    </div>
  );
}

/**
 * Une pastille par compagnon visible : heures chargées / heures de présence sur
 * la période affichée (le jour, ou lun→ven en semaine), avec une jauge.
 * Vert = marge, orange = presque plein, rouge = surchargé ou chargé sans présence.
 * Devant, une jauge par équipe atelier (users.config.team → worker.specialty) :
 * la somme des compagnons visibles de l'équipe.
 */
export function PresenceLoadBar({
  workers,
  dates,
  blocks,
  jobs,
  activeWorkerId,
  onSelectWorker,
}: {
  workers: WorkshopWorker[];
  dates: string[];
  blocks: AvailabilityBlock[];
  jobs: WorkshopJob[];
  activeWorkerId?: string;
  onSelectWorker?: (id: string) => void;
}) {
  if (workers.length === 0) return null;

  const byWorker: Record<string, Load> = {};
  const byTeam: Record<string, Load & { count: number }> = {};
  workers.forEach((w) => {
    let presence = 0;
    let load = 0;
    for (const d of dates) {
      presence += workerPresenceHours(blocks, w.id, d);
      load += workerLoadHours(jobs, w.id, d);
    }
    byWorker[w.id] = { presence, load };
    const team = w.specialty || "autre";
    const t = byTeam[team] || (byTeam[team] = { presence: 0, load: 0, count: 0 });
    t.presence += presence;
    t.load += load;
    t.count += 1;
  });
  const teams = [
    ...ATELIER_FILTER_METIERS.filter((m) => byTeam[m]),
    ...Object.keys(byTeam).filter((m) => !ATELIER_FILTER_METIERS.includes(m)),
  ];

  return (
    <div className="flex flex-row flex-wrap items-stretch" style={{ gap: 6 }}>
      {teams.map((team) => {
        const value = byTeam[team];
        const meta = METIER_META[team] || METIER_META.autre;
        const { color } = loadColor(value);
        const name = `Équipe ${metierLabel(team)}`;
        return (
          <div
            key={`team-${team}`}
            title={`${loadTitle(name, value)} (${value.count} compagnon${value.count > 1 ? "s" : ""})`}
            className="flex flex-col rounded-lg text-left"
            style={{
              fontFamily: "Poppins",
              padding: "4px 8px",
              minWidth: 140,
              gap: 3,
              background: meta.bg,
              border: `1px solid ${meta.border}`,
            }}
          >
            <div className="flex flex-row justify-between items-baseline" style={{ gap: 8 }}>
              <span className="text-xs font-semibold truncate" style={{ color: meta.color, maxWidth: 110 }}>
                {name}
              </span>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
                {fmt(value.load)} / {fmt(value.presence)}
              </span>
            </div>
            <Gauge value={value} />
          </div>
        );
      })}
      <div style={{ width: 1, background: "#E5E5E5", margin: "2px 4px" }} />
      {workers.map((w) => {
        const value = byWorker[w.id];
        const { color } = loadColor(value);
        const active = activeWorkerId === w.id;
        return (
          <button
            key={w.id}
            type="button"
            onClick={onSelectWorker ? () => onSelectWorker(w.id) : undefined}
            title={loadTitle(w.name, value)}
            className="flex flex-col rounded-lg text-left transition-colors"
            style={{
              fontFamily: "Poppins",
              padding: "4px 8px",
              minWidth: 112,
              gap: 3,
              background: "#fff",
              border: `1px solid ${active ? "#A3A3A3" : "#E5E5E5"}`,
              cursor: onSelectWorker ? "pointer" : "default",
            }}
          >
            <div className="flex flex-row justify-between items-baseline" style={{ gap: 8 }}>
              <span className="text-xs font-medium truncate" style={{ color: "#262626", maxWidth: 90 }}>
                {w.name}
              </span>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
                {fmt(value.load)} / {fmt(value.presence)}
              </span>
            </div>
            <Gauge value={value} />
          </button>
        );
      })}
    </div>
  );
}
